import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BookingDetail {
  id: string;
  service_name?: string;
  duration_minutes: number;
  location?: string;
  address?: string;
  booking_date: string;
  start_time: string; // Local display time
  end_time: string; // Local display time
  // TIMEZONE FIX: Preserve original UTC times for backend operations
  utc_start_time: string; // Original UTC time for backend
  utc_end_time: string; // Original UTC time for backend
  status: string;
  price: number;
  currency: string;
  customer_name: string;
  customer_email: string;
  provider_id: string;
  service_id: string;
  user_id: string;
  booking_number?: string;
  updated_at?: string;
  notes?: string;
  checked_in_at?: string;
  checked_out_at?: string;
  created_at?: string;
  customer_phone?: string;
  stripe_session_id?: string;
  time_slot_id?: string; // CRITICAL: Add this field to the interface
  providers?: {
    name: string;
    address: string;
  };
  services?: {
    name: string;
  };
}

export const useBooking = (bookingId?: string) => {
  return useQuery({
    queryKey: ["booking", bookingId],
    queryFn: async () => {
      if (!bookingId) {
        throw new Error("Booking ID is required");
      }

      console.log("Fetching booking details for ID:", bookingId);
      
      const { data: { user } } = await supabase.auth.getUser();
      console.log("Current user:", user?.email);
      
      // First try to fetch the booking to see if it exists
      // CRITICAL FIX: Include time_slot_id in the select query
      let query = supabase
        .from('bookings')
        .select(`
          *,
          time_slot_id,
          providers:provider_id(name, address),
          services:service_id(name)
        `)
        .eq('id', bookingId);
      
      // If user is authenticated, filter by user_id for security
      if (user) {
        query = query.eq('user_id', user.id);
      }

      const { data: booking, error } = await query.single();

      console.log("Booking query result:", booking);
      console.log("Booking query error:", error);

      if (error) {
        console.error('Error fetching booking:', error);
        
        // If no user and we get a "no rows" error, it might be a shared link
        if (!user && error.code === 'PGRST116') {
          console.log("No authenticated user - this might be a shared booking link");
          throw new Error("Please sign in to view your booking details");
        }
        
        throw error;
      }

      if (!booking) {
        throw new Error("Booking not found");
      }

      // TIMEZONE FIX: Properly handle UTC times from database
      // Times are stored as HH:MM:SS in UTC, convert to local time for display only
      const utcStartISO = `${booking.booking_date}T${booking.start_time}Z`;
      const utcEndISO = `${booking.booking_date}T${booking.end_time}Z`;
      
      // Convert to local time for display
      const localStartTime = new Date(utcStartISO).toLocaleTimeString('en-GB', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit'
      });
      const localEndTime = new Date(utcEndISO).toLocaleTimeString('en-GB', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit'
      });
      
      console.log('BOOKING HOOK: Time conversion for booking', booking.id, {
        booking_date: booking.booking_date,
        db_start_time: booking.start_time,
        db_end_time: booking.end_time,
        utc_start_iso: utcStartISO,
        utc_end_iso: utcEndISO,
        local_start_display: localStartTime,
        local_end_display: localEndTime,
        time_slot_id: booking.time_slot_id, // Log the time_slot_id
        timezone_info: {
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          utc_offset_minutes: new Date().getTimezoneOffset()
        }
      });

      // Transform the data to match the expected format
      const transformedBooking: BookingDetail = {
        id: booking.id,
        service_name: booking.services?.name || 'Unknown Service',
        duration_minutes: booking.duration_minutes,
        location: booking.providers?.name || 'Unknown Location',
        address: booking.providers?.address || '',
        booking_date: booking.booking_date,
        start_time: localStartTime, // Local display time
        end_time: localEndTime, // Local display time
        // TIMEZONE FIX: Preserve UTC times as full ISO strings for backend operations
        utc_start_time: utcStartISO, // Full UTC ISO string for backend
        utc_end_time: utcEndISO, // Full UTC ISO string for backend
        status: booking.status === 'confirmed' ? 'Confirmed' : 
                booking.status === 'completed' ? 'Completed' : 
                booking.status === 'cancelled' ? 'Cancelled' : booking.status,
        price: Number(booking.price),
        currency: booking.currency || 'GBP',
        customer_name: booking.customer_name,
        customer_email: booking.customer_email,
        provider_id: booking.provider_id,
        service_id: booking.service_id,
        user_id: booking.user_id,
        booking_number: booking.booking_number,
        updated_at: booking.updated_at,
        notes: booking.notes,
        checked_in_at: booking.checked_in_at,
        checked_out_at: booking.checked_out_at,
        created_at: booking.created_at,
        customer_phone: booking.customer_phone,
        stripe_session_id: booking.stripe_session_id,
        time_slot_id: booking.time_slot_id, // CRITICAL: Include time_slot_id in transformed booking
        providers: booking.providers,
        services: booking.services
      };

      console.log("Transformed booking with time_slot_id:", {
        id: transformedBooking.id,
        time_slot_id: transformedBooking.time_slot_id,
        // ... other fields for debugging
      });
      
      return transformedBooking;
    },
    enabled: !!bookingId,
    retry: false,
  });
};
